import { activityFlatsOfModels } from '@components/pages/activity-log/services/activity-log-flat.util';
import { GuestInfo } from '@models/guest-info.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HostActivityLogService } from '../../../shared/host-activity-log.service';
import { messageSendMetadata } from '../../../shared/host-activity.metadata';
import { CommunicationModalService, CommunicationRequest } from './communication-modal.service';

interface UseCase {
  id: string;
  label: string;
  hasLink: boolean;
}

interface CommunicationChannel {
  id: 'email' | 'sms';
  label: string;
  icon: string;
}

interface Language {
  id: string;
  label: string;
}

@Component({
  selector: 'app-communication-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    InputTextarea,
    TooltipModule,
    ToastModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      styleClass="w-full max-w-4xl"
      (onHide)="onClose()"
      (visibleChange)="onVisibilityChange($event)"
    >
      <ng-template pTemplate="header">
        <div class="flex items-center gap-3">
          <i class="pi pi-comments text-xl text-primary"></i>
          <span class="text-xl font-semibold">{{ 'communication.title' | translate }}</span>
        </div>
      </ng-template>

      @if (guestInfo()) {
        <div class="space-y-6">
          <!-- Guest Info Header (2-column layout) -->
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <h3 class="mb-4 text-lg font-medium text-gray-900 dark:text-white">{{ 'communication.guestInfo' | translate }}</h3>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <!-- Left Column -->
              <div class="space-y-3">
                <!-- Name -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <i class="pi pi-user text-gray-500"></i>
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'common.name' | translate }}:</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-900 dark:text-gray-300">{{ guestInfo()?.name || '-' }}</span>
                    <button
                      pButton
                      icon="pi pi-copy"
                      class="p-button-text p-button-sm"
                      pTooltip="{{ 'common.copyToClipboard' | translate }}"
                      (click)="copyToClipboard(guestInfo()?.name || '')"
                    ></button>
                  </div>
                </div>

                <!-- Email -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <i class="pi pi-envelope text-gray-500"></i>
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'common.email' | translate }}:</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-900 dark:text-gray-300">{{ guestInfo()?.email || '-' }}</span>
                    @if (guestInfo()?.email) {
                      <button
                        pButton
                        icon="pi pi-copy"
                        class="p-button-text p-button-sm"
                        pTooltip="{{ 'common.copyToClipboard' | translate }}"
                        (click)="copyToClipboard(guestInfo()?.email || '')"
                      ></button>
                    }
                  </div>
                </div>
              </div>

              <!-- Right Column -->
              <div class="space-y-3">
                <!-- Phone -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <i class="pi pi-phone text-gray-500"></i>
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'common.phone' | translate }}:</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-900 dark:text-gray-300">{{ guestInfo()?.phone || '-' }}</span>
                    @if (guestInfo()?.phone) {
                      <button
                        pButton
                        icon="pi pi-copy"
                        class="p-button-text p-button-sm"
                        pTooltip="{{ 'common.copyToClipboard' | translate }}"
                        (click)="copyToClipboard(guestInfo()?.phone || '')"
                      ></button>
                    }
                  </div>
                </div>

                <!-- Last App Login -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <i class="pi pi-mobile text-gray-500"></i>
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'guestInfo.lastAppLogin' | translate }}:</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-900 dark:text-gray-300">
                      {{ guestInfo()?.lastAppLogin ? formatAppLogin(guestInfo()!.lastAppLogin!) : '-' }}
                    </span>
                    <!-- Invisible placeholder matching the copy button dimensions -->
                    <div class="invisible h-[2rem] w-[2rem]"></div>
                  </div>
                </div>

                <!-- Mobile app login code -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <i class="pi pi-mobile text-gray-500"></i>
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'booking.loginToken.label' | translate }}:</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="font-mono text-sm font-semibold tracking-wider text-gray-900 dark:text-gray-300">
                      {{ booking()?.login_token || '-' }}
                    </span>
                    @if (booking()?.login_token) {
                      <button
                        pButton
                        icon="pi pi-copy"
                        class="p-button-text p-button-sm"
                        pTooltip="{{ 'common.copyToClipboard' | translate }}"
                        (click)="copyToClipboard(booking()?.login_token || '')"
                      ></button>
                    } @else {
                      <div class="invisible h-[2rem] w-[2rem]"></div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Communication Options -->
          <div class="space-y-4">
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-300">{{ 'communication.options' | translate }}</h3>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
              <!-- Use Case Selection -->
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ 'communication.useCase' | translate }}
                </label>
                <p-dropdown
                  [options]="useCases()"
                  [(ngModel)]="selectedUseCase"
                  optionLabel="label"
                  optionValue="id"
                  placeholder="{{ 'communication.selectUseCase' | translate }}"
                  styleClass="w-full"
                >
                  <ng-template
                    pTemplate="item"
                    let-option
                  >
                    {{ option.label | translate }}
                  </ng-template>
                  <ng-template
                    pTemplate="selectedItem"
                    let-option
                  >
                    {{ option.label | translate }}
                  </ng-template>
                </p-dropdown>
              </div>

              <!-- Communication Channel -->
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ 'communication.channel' | translate }}
                </label>
                <p-dropdown
                  [options]="channels"
                  [(ngModel)]="selectedChannel"
                  optionLabel="label"
                  optionValue="id"
                  placeholder="{{ 'communication.selectChannel' | translate }}"
                  styleClass="w-full"
                >
                  <ng-template
                    pTemplate="item"
                    let-option
                  >
                    {{ option.label | translate }}
                  </ng-template>
                  <ng-template
                    pTemplate="selectedItem"
                    let-option
                  >
                    {{ option.label | translate }}
                  </ng-template>
                </p-dropdown>
              </div>

              <!-- Language -->
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ 'communication.language' | translate }}
                </label>
                <p-dropdown
                  [options]="languages"
                  [(ngModel)]="selectedLanguage"
                  optionLabel="label"
                  optionValue="id"
                  placeholder="{{ 'communication.selectLanguage' | translate }}"
                  styleClass="w-full"
                  [disabled]="isLanguageServerDecided()"
                >
                  <ng-template
                    pTemplate="item"
                    let-option
                  >
                    {{ option.label | translate }}
                  </ng-template>
                  <ng-template
                    pTemplate="selectedItem"
                    let-option
                  >
                    {{ option.label | translate }}
                  </ng-template>
                </p-dropdown>
                @if (isLanguageServerDecided()) {
                  <small class="mt-1 text-xs text-gray-500 dark:text-gray-300">
                    {{ languageInfoText() }}
                  </small>
                }
              </div>
            </div>

            <!-- Recipient -->
            <div>
              <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ 'communication.recipient' | translate }}
              </label>
              <input
                type="text"
                [(ngModel)]="recipient"
                [placeholder]="getRecipientPlaceholder()"
                [readonly]="isRecipientReadonly()"
                class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800"
                [class.bg-gray-100]="isRecipientReadonly()"
              />
              @if (isRecipientReadonly()) {
                <small class="mt-1 text-xs text-gray-500 dark:text-gray-300">
                  {{ recipientInfoText() }}
                </small>
              }
            </div>

            <!-- Copyable Link (shown for check-in and parking request links) -->
            @if (showLink()) {
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ 'communication.generatedLink' | translate }}
                </label>
                <div class="flex gap-2">
                  <input
                    type="text"
                    [value]="generatedLink()"
                    readonly
                    class="flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
                  />
                  <button
                    pButton
                    icon="pi pi-copy"
                    class="p-button-outlined"
                    pTooltip="{{ 'common.copyToClipboard' | translate }}"
                    (click)="copyToClipboard(generatedLink())"
                  ></button>
                </div>
              </div>
            }

            <!-- Message Preview -->
            <div>
              <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ 'communication.messagePreview' | translate }}
              </label>
              <textarea
                pInputTextarea
                [value]="messagePreview()"
                readonly
                rows="4"
                class="w-full"
              ></textarea>
            </div>
          </div>
        </div>
      }

      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2">
          <button
            pButton
            type="button"
            label="{{ 'common.cancel' | translate }}"
            class="p-button-outlined"
            (click)="onClose()"
          ></button>
          <button
            pButton
            type="button"
            label="{{ 'communication.send' | translate }}"
            [disabled]="!canSend()"
            (click)="onSend()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class CommunicationModalComponent {
  // Services
  private communicationService = inject(CommunicationModalService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private readonly hostActivityLog = inject(HostActivityLogService);

  // Inputs
  visible = input<boolean>(false);
  guestInfo = input<GuestInfo | null>(null);
  booking = input<IBookingModel | null>(null);
  entryCodes = input<string[]>([]);

  // Outputs
  closed = output<void>();

  // Component state
  selectedUseCase = signal<string>('entry-code'); // Default to entry code
  selectedChannel = signal<'email' | 'sms'>('email');
  selectedLanguage = signal<string>('en');
  recipient = signal<string>('');
  isLoading = signal<boolean>(false);

  // Computed properties
  messagePreview = computed(() => {
    // Use nationality-based language if server-decided
    const language = this.isLanguageServerDecided() ? this.nationalityBasedLanguage() : this.selectedLanguage();

    const preview = this.communicationService.getMessagePreview(
      this.selectedUseCase(),
      this.selectedChannel(),
      language,
      this.booking(),
      this.entryCodes()
    );
    return preview.text;
  });

  generatedLink = computed(() => {
    // Use nationality-based language if server-decided
    const language = this.isLanguageServerDecided() ? this.nationalityBasedLanguage() : this.selectedLanguage();

    const preview = this.communicationService.getMessagePreview(
      this.selectedUseCase(),
      this.selectedChannel(),
      language,
      this.booking(),
      this.entryCodes()
    );
    return preview.link || '';
  });

  isRecipientReadonly = computed(() => {
    return this.communicationService.isRecipientReadonly(this.selectedUseCase(), this.selectedChannel());
  });

  recipientInfoText = computed(() => {
    return this.isRecipientReadonly() ? this.communicationService.getRecipientInfoText() : '';
  });

  isLanguageServerDecided = computed(() => {
    return this.communicationService.isLanguageServerDecided(this.selectedUseCase(), this.selectedChannel());
  });

  languageInfoText = computed(() => {
    return this.isLanguageServerDecided() ? this.communicationService.getLanguageInfoText() : '';
  });

  nationalityBasedLanguage = computed(() => {
    const booking = this.booking();
    const nationality = booking?.nationality;
    return this.communicationService.getLanguageFromNationality(nationality);
  });

  isImplemented = computed(() => {
    return this.communicationService.isImplemented(this.selectedUseCase(), this.selectedChannel());
  });

  showLink = computed(() => {
    const useCase = this.selectedUseCase();
    return useCase === 'checkin-link' || useCase === 'parking-link';
  });

  // All available use cases
  private allUseCases: UseCase[] = [
    { id: 'entry-code', label: 'communication.sendEntryCode', hasLink: false },
    { id: 'checkin-link', label: 'communication.sendCheckInLink', hasLink: true },
    { id: 'parking-link', label: 'communication.sendParkingLink', hasLink: true }
  ];

  // Filtered use cases: hide entry-code when no entry codes are available
  useCases = computed(() => {
    const codes = this.entryCodes();
    if (codes && codes.length > 0) {
      return this.allUseCases;
    }
    return this.allUseCases.filter((uc) => uc.id !== 'entry-code');
  });

  channels: CommunicationChannel[] = [
    { id: 'email', label: 'communication.email', icon: 'pi-envelope' },
    { id: 'sms', label: 'communication.sms', icon: 'pi-mobile' }
  ];

  languages: Language[] = [
    { id: 'en', label: 'communication.english' },
    { id: 'hu', label: 'communication.hungarian' }
  ];

  constructor() {
    // Initialize recipient when guestInfo changes
    effect(() => {
      const info = this.guestInfo();
      if (info) {
        this.updateRecipient();
      }
    });

    // Update recipient when channel changes
    effect(() => {
      this.selectedChannel(); // Track the signal
      this.updateRecipient();
    });

    // Reset use case selection when the current selection is no longer available
    effect(() => {
      const available = this.useCases();
      const current = this.selectedUseCase();
      if (!available.find((uc) => uc.id === current)) {
        this.selectedUseCase.set('checkin-link');
      }
    });

    // Update selected language based on nationality when use case or channel changes
    effect(() => {
      const useCase = this.selectedUseCase();
      const channel = this.selectedChannel();
      const isServerDecided = this.communicationService.isLanguageServerDecided(useCase, channel);

      if (isServerDecided) {
        const booking = this.booking();
        if (booking) {
          const nationality = booking.nationality;
          const language = this.communicationService.getLanguageFromNationality(nationality);
          this.selectedLanguage.set(language);
        }
      }
    });
  }

  formatAppLogin(date: Date): string {
    if (!date) return '';
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  }

  copyToClipboard(text: string): void {
    if (text && navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  }

  getRecipientPlaceholder(): string {
    return this.selectedChannel() === 'email' ? 'Enter email address' : 'Enter phone number';
  }

  canSend(): boolean {
    return !!(this.selectedUseCase() && this.selectedChannel() && this.selectedLanguage() && this.recipient() && !this.isLoading());
  }

  private updateRecipient(): void {
    const info = this.guestInfo();
    if (!info) return;

    if (this.selectedChannel() === 'email') {
      this.recipient.set(info.email || '');
    } else {
      this.recipient.set(info.phone || '');
    }
  }

  onClose(): void {
    this.closed.emit();
  }

  onVisibilityChange(visible: boolean): void {
    if (!visible) {
      this.onClose();
    }
  }

  onSend(): void {
    if (!this.canSend()) return;

    // Use nationality-based language when the language is server-decided
    const language = this.isLanguageServerDecided() ? this.nationalityBasedLanguage() : this.selectedLanguage();

    const request: CommunicationRequest = {
      useCase: this.selectedUseCase(),
      channel: this.selectedChannel(),
      language: language,
      recipient: this.recipient()
    };

    const booking = this.booking();
    const guestInfo = this.guestInfo();

    if (!booking || !guestInfo) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error') as string,
        detail: this.translateService.instant('communication.error.missingInfo') as string
      });
      return;
    }

    // Check if use case is implemented
    if (!this.isImplemented()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Not Implemented',
        detail: 'This use case + channel combination is not yet implemented.'
      });
      return;
    }

    this.isLoading.set(true);

    this.communicationService.sendMessage(request, booking, guestInfo).subscribe({
      next: (result) => {
        const { useCase, channel, language } = request;
        const flats = activityFlatsOfModels(booking.flats);
        this.hostActivityLog.log('message_send', messageSendMetadata({ bookingId: booking.id, flats, useCase, channel, language }));
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success') as string,
          detail: result.message
        });
        this.isLoading.set(false);
        this.onClose(); // Close modal on success
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error') as string,
          detail: error.message || (this.translateService.instant('communication.error.sendFailed') as string)
        });
        this.isLoading.set(false);
        // Keep modal open on error
      }
    });
  }
}
